import { useTimeSeriesTrend } from '../../../../../hooks/useDataQuality'
import { MetricType } from '../../../../../types/dataQuality'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import LoadingSkeleton from '../../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../../common/ErrorDisplay'
import './TimelinessChart.css'

const TimelinessChart = () => {
  const { data, isLoading, error } = useTimeSeriesTrend({
    metric_type: MetricType.FRESHNESS_SCORE,
    days_back: 7,
    granularity: 'daily',
    include_trend_line: false
  })

  if (isLoading) {
    return (
      <div className="timeliness-dashboard">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Timeliness</h3>
        </div>
        <LoadingSkeleton variant="chart" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="timeliness-dashboard">
        <ErrorDisplay
          error={error.message}
          onRetry={() => window.location.reload()}
          title="Failed to Load Timeliness Data"
        />
      </div>
    )
  }

  if (!data || !data.chart_data || data.chart_data.length === 0) {
    return (
      <div className="timeliness-dashboard">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Timeliness</h3>
          <p className="section-subtitle-compact">On-time data arrival vs SLA</p>
        </div>
        <p className="no-data-message">No timeliness data available</p>
      </div>
    )
  }

  // Format date to MM/DD
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return `${date.getMonth() + 1}/${date.getDate()}`
  }

  // Transform data for recharts
  const chartData = data.chart_data.map(point => ({
    date: formatDate(point.date),
    value: point.value,
    fullDate: point.date
  }))

  const averageValue = data.summary?.average_value || 0
  const slaTarget = 98 // As per integration guide

  // Calculate trend based on recent data points
  const calculateTrend = (): 'up' | 'down' | 'neutral' => {
    if (chartData.length < 2) return 'neutral'
    
    // Compare the last 2 data points for immediate trend
    const lastPoint = chartData[chartData.length - 1].value
    const previousPoint = chartData[chartData.length - 2].value
    
    // More sensitive threshold for trend detection
    const threshold = 0.01 // 0.01% difference is enough to show trend
    
    if (lastPoint > previousPoint + threshold) return 'up'
    if (lastPoint < previousPoint - threshold) return 'down'
    
    // If last 2 points are equal, check last 3 vs previous 3
    if (chartData.length >= 6) {
      const recentData = chartData.slice(-3)
      const olderData = chartData.slice(-6, -3)
      
      const recentAvg = recentData.reduce((sum, point) => sum + point.value, 0) / recentData.length
      const olderAvg = olderData.reduce((sum, point) => sum + point.value, 0) / olderData.length
      
      if (recentAvg > olderAvg + threshold) return 'up'
      if (recentAvg < olderAvg - threshold) return 'down'
    }
    
    return 'neutral'
  }

  const trend = calculateTrend()

  return (
    <div className="timeliness-dashboard">
      <div className="timeliness-header-row">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Timeliness</h3>
          <p className="section-subtitle-compact">On-time data arrival vs SLA</p>
        </div>
        <div className="timeliness-sla-badge">
          SLA Target: <span className="timeliness-sla-value">&gt; {slaTarget}%</span>
        </div>
      </div>

      <div className="timeliness-main-score">
        <div className="timeliness-score-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="timeliness-score-large">{averageValue.toFixed(1)}%</span>
            <span className="timeliness-status-text" style={{ 
              fontSize: '14px', 
              color: averageValue >= slaTarget ? '#00C950' : '#EF4444',
              fontWeight: 500
            }}>
              on time
            </span>
          </div>
          <div>
            {trend === 'up' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                <polyline points="17 6 23 6 23 12"/>
              </svg>
            )}
            {trend === 'down' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
                <polyline points="17 18 23 18 23 12"/>
              </svg>
            )}
            {trend === 'neutral' && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            )}
          </div>
        </div>
      </div>

      <div className="timeliness-chart-container">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorTimeliness" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              stroke="#9ca3af"
              style={{ fontSize: '12px' }}
              tick={{ fill: '#6b7280' }}
            />
            <YAxis
              domain={[95, 100]}
              stroke="#9ca3af"
              style={{ fontSize: '12px' }}
              tick={{ fill: '#6b7280' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
              formatter={(value: any) => typeof value === 'number' ? [`${value.toFixed(1)}%`, 'Timeliness'] : ['', '']}
              labelStyle={{ color: '#111827', fontWeight: 600 }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#10b981"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorTimeliness)"
              animationDuration={1500}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {data.summary && (
        <div className="timeliness-summary">
          <div className="timeliness-summary-item">
            <span className="timeliness-summary-label">Best</span>
            <span className="timeliness-summary-value">{data.summary.best_value.toFixed(1)}%</span>
          </div>
          <div className="timeliness-summary-item">
            <span className="timeliness-summary-label">Worst</span>
            <span className="timeliness-summary-value">{data.summary.worst_value.toFixed(1)}%</span>
          </div>
          <div className="timeliness-summary-item">
            <span className="timeliness-summary-label">Avg</span>
            <span className="timeliness-summary-value">{data.summary.average_value.toFixed(1)}%</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default TimelinessChart
